import React, { useEffect, useState } from "react";
import "./Home.css";
import { useNavigate } from "react-router-dom";

import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

import { useDispatch, useSelector } from "react-redux";

import { getAllCategories } from "../../features/master/masterSlice";
import {
  getAllTopProducts,
  getAllTrandingProducts,
} from "../../features/products/productsSlice";
import {
  getCsrBanner,
  getCorporateBanner,
  getFillerBanner,
} from "../../features/home/homeSlice";

import New_arrival_card from "./top-frames/New_arrival_card";
import Offer_banner from "./offer-banner/Offer_banner";


import VirtualTryOn from "../virtual-try-on/Virtual_try_on";
import { get3DImage } from "../../utils/common";

import "./new-arrivals-section.css";

/* Number of cards visible at once, matching the CSS breakpoints:
   3 on desktop, 2 on tablet, 1 on mobile. */
const useArrivalsVisible = () => {
  const get = () =>
    window.matchMedia("(max-width: 767.98px)").matches
      ? 1
      : window.matchMedia("(max-width: 991.98px)").matches
        ? 2
        : 3;

  const [count, setCount] = useState(get);

  useEffect(() => {
    const onResize = () => setCount(get());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return count;
};

const firstBanner = (b) =>
  Array.isArray(b) ? b.find((x) => x && x.status !== false) : b;

const formatBannerTitle = (name) =>
  typeof name === "string" ? name.replace(/_/g, " ") : name;

/* ==========================================================
   PRODUCT CAROUSEL
   Used for both "New Arrivals" and "Trending Now",
   so they look identical.
   ========================================================== */

const ProductCarousel = ({
  sectionId,
  titlePrefix,
  titleAccent,
  products,
  onTryOn,
}) => {
  const visibleCount = useArrivalsVisible();
  const [startIndex, setStartIndex] = useState(0);

  const maxStart = Math.max((products?.length || 0) - visibleCount, 0);

  // clamp in case the screen was resized while scrolled to the end
  const start = Math.min(startIndex, maxStart);

  const visibleItems = products?.slice(start, start + visibleCount) || [];

  const shift = (direction) => {
    setStartIndex(Math.min(Math.max(start + direction, 0), maxStart));
  };

  if (!products || products.length === 0) return null;

  return (
    <section id={sectionId} className="new_arrivals_sec">
      <div className="container-xl">
        <div className="new_arrivals_header">
          <h2 className="new_arrivals_title">
            {titlePrefix}{" "}
            <span className="new_arrivals_title_accent">{titleAccent}</span>
          </h2>
        </div>
      </div>

      <div className="new_arrivals_carousel">
        <button
          type="button"
          className="nav_side_arrow nav_side_arrow_left"
          onClick={() => shift(-1)}
          disabled={start <= 0}
          aria-label={`Show previous ${titleAccent}`}
        >
          <FaArrowLeft />
        </button>

        <div className="container-xl">
          <div className="new_arrivals_grid">
            {visibleItems.map((product) => (
              <div className="new_arrivals_item" key={product.id}>
                <New_arrival_card
                  {...product}
                  setSelectedProduct={() => onTryOn?.(product)}
                />
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="nav_side_arrow nav_side_arrow_right"
          onClick={() => shift(1)}
          disabled={start >= maxStart}
          aria-label={`Show next ${titleAccent}`}
        >
          <FaArrowRight />
        </button>
      </div>
    </section>
  );
};

/* ==========================================================
   HOME COMPONENT
   ========================================================== */

function Home({ handleFilterChange }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = useSelector((state) => state.master.categories);

  const topProducts = useSelector((state) => state.product.allProductsTop);

  const trendingRaw = useSelector((state) => state.product.trendingProducts);

  const csrBanner = useSelector((state) => state.home.csrBanner);

  const corporateBanner = useSelector((state) => state.home.corporateBanner);

 // Filler banner (banner_type_id 6), shown below the New Arrivals section
  const fillerBanner = useSelector((state) => state.home.fillerBanner);

  useEffect(() => {
    dispatch(getAllCategories());
    dispatch(getAllTopProducts({}));
    dispatch(getAllTrandingProducts({}));
    dispatch(getCsrBanner({ banner_type_id: 4 }));
    dispatch(getCorporateBanner({ banner_type_id: 5 }));
    dispatch(getFillerBanner({ banner_type_id: 7 }));
  }, [dispatch]);

  const handleCategoryClick = (category) => {
    if (!handleFilterChange) return;

    handleFilterChange("category", {
      id: category.id,
      name: category.name,
    });

    navigate("/all-products");
  };

  const categoryCards =
    categories && categories.length > 0 ? categories.slice(0, 4) : [];


  const [selectedProduct, setSelectedProduct] = useState(null);
  const tryOnImage = selectedProduct
    ? get3DImage(selectedProduct?.Product_Images) || ""
    : "";

  // Some endpoints wrap the product as { Product, Gender }; flatten so the
  // card always receives a plain product object.
  const trendingProducts = Array.isArray(trendingRaw)
    ? trendingRaw.map((item) => (item?.Product ? { ...item.Product } : item))
    : [];

  const videoCards = [csrBanner, corporateBanner]
    .map(firstBanner)
    .filter((b) => b && b.image)
    .map((b) => ({
      id: b.id,
      title: formatBannerTitle(b.name),
      src: process.env.REACT_APP_IMAGE_URL + b.image,
    }));

  const filler = firstBanner(fillerBanner);
  const fillerSrc =
    filler && filler.image
      ? process.env.REACT_APP_IMAGE_URL + filler.image
      : null;

  return (
    <>
      <Offer_banner />

      {/* NEW ARRIVALS */}
      <ProductCarousel
        sectionId="new-arrivals"
        titlePrefix="Shop by"
        titleAccent="new arrivals"
        products={topProducts}
        onTryOn={setSelectedProduct}
      />

      {/* FILLER BANNER */}
      {fillerSrc && (
        <section className="filler_banner_sec">
          <img
            className="filler_banner_img"
            src={fillerSrc}
            alt={filler?.name ? formatBannerTitle(filler.name) : "Offer banner"}
          />
        </section>
      )}

      {/* CATEGORY CARDS */}
      <section className="category_cards_sec">
        <div className="container-xl">
          <div className="category_cards_grid">
            {categoryCards.length > 0 ? (
              categoryCards.map((category) => (
                <div
                  className="category_card"
                  key={category.id}
                  onClick={() => handleCategoryClick(category)}
                >
                  <img
                    className="category_card_img"
                    src={process.env.REACT_APP_IMAGE_URL + category.image}
                    alt={category.name || "Category"}
                  />

                  <p className="category_card_label">{category.name}</p>
                </div>
              ))
            ) : (
              <div className="no-data">No categories yet</div>
            )}
          </div>
        </div>
      </section>

      {/* TRENDING NOW */}
      <ProductCarousel
        sectionId="trending-now"
        titlePrefix="Trending"
        titleAccent="now"
        products={trendingProducts}
        onTryOn={setSelectedProduct}
      />

      {/* PROMOTIONAL VIDEOS */}
      {videoCards.length > 0 && (
        <section className="video_cards_sec">
          <div className="video_cards_grid">
            {videoCards.map((card) => (
              <VideoCard card={card} key={card.id ?? card.src} />
            ))}
          </div>
        </section>
      )}

      
      {/* NEW: try-on modal */}
{selectedProduct && (
  <VirtualTryOn
    isOpen={!!selectedProduct}
    onClose={() => setSelectedProduct(null)}
    category={selectedProduct?.p_category}
    brand={selectedProduct?.Brand}
    size={selectedProduct?.size}
    total_measurements={selectedProduct?.total_measurements}
    productImages={selectedProduct?.Product_Images}   // NEW
    glbModal={tryOnImage}
    is3DVTO={selectedProduct?.is_3D_VTO}              // NEW
    product={selectedProduct}                         // NEW
  />
)}
    </>
  );
}

/* ==========================================================
   VIDEO CARD - silent autoplay/loop, matching the hero video
   ========================================================== */

const VideoCard = ({ card }) => {
  return (
    <div className="video_card">
      <video
        className="video_card_media"
        src={card.src}
        autoPlay
        muted
        loop
        playsInline
      />
      {card.title && <p className="video_card_title">{card.title}</p>}
    </div>
  );
};

export default Home;